/**
 * Asistente de 9 pasos, reconstruido sobre el backend nuevo (antes vivía en
 * este mismo archivo + wizard/fields.tsx + wizard/steps.tsx contra Supabase,
 * ver historial git previo al corte). Cada paso se guarda de inmediato al
 * avanzar (no hay borrador local con guardado batch al final) -- más simple
 * y sin riesgo de perder una sesión larga completa si el navegador se
 * cierra a medio llenar.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DATA_CATEGORIES as STATIC_DATA_CATEGORIES,
  LEGAL_BASES as STATIC_LEGAL_BASES,
  RECIPIENT_TYPES as STATIC_RECIPIENT_TYPES,
  RETENTION_CRITERIA as STATIC_RETENTION_CRITERIA,
  SECURITY_MEASURES as STATIC_SECURITY_MEASURES,
  SUBJECT_CATEGORIES as STATIC_SUBJECT_CATEGORIES,
  TRANSFER_GUARANTEE_TYPES as STATIC_TRANSFER_GUARANTEE_TYPES,
  EMPTY_WIZARD_DRAFT,
  WIZARD_STEPS,
  makeActivitySubmitSchema,
  wizardStepSchemas,
  type ActivityStatus,
  type WizardDraft,
  type WizardStepKey,
} from '@rat/shared';
import {
  createActivityApi,
  fetchActivity,
  fetchCatalogs,
  fetchAssignableUnits,
  saveAutomatedDecisions,
  saveDataCategories,
  saveLegalBases,
  saveRecipients,
  saveSecurityMeasures,
  saveSubjectCategories,
  saveTransfers,
  fetchOpenObservations,
  transitionActivity,
  updateActivityCore,
  type ActivityDetailResponse,
  type PublicCatalogs,
} from '../lib/queries';
import { useAuth } from '../auth/AuthProvider';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { PageHeader, QueryState } from '../components/ui';
import {
  StepAutomatizacion,
  StepBaseJuridica,
  StepConservacion,
  StepDestinatarios,
  StepFinalidad,
  StepIdentificacion,
  StepSeguridad,
  StepTitulares,
  type StepProps,
} from './wizard/steps';

const EDITABLE_STATUS = new Set<ActivityStatus>(['BORRADOR', 'EN_COMPLETADO', 'OBSERVADO', 'CORREGIDO']);

/**
 * Respaldo mientras GET /catalogs carga (o si falla) -- los catalogos son
 * administrables ahora (src/catalog-admin.ts), pero el asistente no puede
 * quedarse en blanco durante ese primer fetch. Mismos datos con los que se
 * sembro la tabla `catalog_items`, convertidos a la forma publica (claves
 * de bandera snake_case) que ya devuelve el backend.
 */
const FALLBACK_CATALOGS: PublicCatalogs = {
  legal_basis: STATIC_LEGAL_BASES.map((c) => ({ code: c.code, label: c.label, requires_reinforced: c.requiresReinforced })),
  data_category: STATIC_DATA_CATEGORIES.map((c) => ({ code: c.code, label: c.label, is_sensitive: c.isSensitive })),
  subject_category: STATIC_SUBJECT_CATEGORIES.map((c) => ({ code: c.code, label: c.label, is_protected_group: c.isProtectedGroup })),
  recipient_type: STATIC_RECIPIENT_TYPES.map((c) => ({ code: c.code, label: c.label, is_processor: c.isProcessor })),
  security_measure: STATIC_SECURITY_MEASURES.map((c) => ({ code: c.code, label: c.label })),
  transfer_guarantee_type: STATIC_TRANSFER_GUARANTEE_TYPES.map((c) => ({ code: c.code, label: c.label })),
  retention_criterion: STATIC_RETENTION_CRITERIA.map((c) => ({ code: c.code, label: c.label })),
};

function detailToDraft(a: ActivityDetailResponse): WizardDraft {
  return {
    ...EMPTY_WIZARD_DRAFT,
    title: a.title ?? '',
    responsible_unit_id: a.responsible_unit_id,
    operational_owner_name: a.operational_owner_name ?? '',
    purpose: a.purpose ?? '',
    description: a.description ?? '',
    data_source: a.data_source ?? '',
    subject_categories: a.subject_categories.map((s) => s.subject_category.code),
    data_categories: a.data_categories.map((c) => c.data_category.code),
    legal_bases: a.legal_bases.map((b) => ({ legal_basis: b.legal_basis.code, justification: '' })),
    recipients: a.recipients.map((r) => ({
      recipient_type: r.recipient_type?.code ?? '',
      name: r.name ?? '',
      is_processor: r.is_processor,
      detail: r.detail ?? '',
    })),
    transfers: a.transfers.map((t) => ({ country: t.country, guarantee_type: t.guarantee_type?.code, detail: t.detail ?? '' })),
    retention_criterion: a.retention_criterion,
    retention_value: a.retention_value,
    retention_unit: a.retention_unit as WizardDraft['retention_unit'],
    retention_text: a.retention_text ?? '',
    has_automated_decision: a.has_automated_decision,
    uses_ai: a.uses_ai,
    automated_decisions: a.automated_decisions.map((d) => ({
      description: d.description,
      stage: d.stage ?? '',
      human_in_the_loop: d.human_in_the_loop,
      produces_profiling: d.produces_profiling,
    })),
    own_systems_text: a.own_systems_text ?? '',
    security_measures: a.security_measures.map((m) => m.security_measure.code),
  };
}

async function saveStep(id: string, key: WizardStepKey, draft: WizardDraft, catalogs: PublicCatalogs): Promise<void> {
  switch (key) {
    case 'identificacion':
      await updateActivityCore(id, { title: draft.title, operational_owner_name: draft.operational_owner_name });
      return;
    case 'finalidad':
      await updateActivityCore(id, { purpose: draft.purpose, description: draft.description, data_source: draft.data_source });
      return;
    case 'titulares':
      await Promise.all([
        saveSubjectCategories(
          id,
          draft.subject_categories.map((code) => ({
            category: code,
            is_protected_group: catalogs.subject_category.find((c) => c.code === code)?.is_protected_group === true,
          })),
        ),
        saveDataCategories(
          id,
          draft.data_categories.map((code) => ({
            category: code,
            is_sensitive: catalogs.data_category.find((c) => c.code === code)?.is_sensitive === true,
          })),
        ),
      ]);
      return;
    case 'base_juridica':
      await saveLegalBases(
        id,
        draft.legal_bases.map((b) => ({
          legal_basis: b.legal_basis,
          is_reinforced: catalogs.legal_basis.find((l) => l.code === b.legal_basis)?.requires_reinforced === true,
        })),
      );
      return;
    case 'destinatarios':
      await Promise.all([
        saveRecipients(id, draft.recipients),
        saveTransfers(id, draft.transfers),
        updateActivityCore(id, { has_international_transfer: draft.transfers.length > 0 }),
      ]);
      return;
    case 'conservacion':
      await updateActivityCore(id, {
        retention_value: draft.retention_value,
        retention_unit: draft.retention_unit,
        retention_criterion: draft.retention_criterion,
        retention_text: draft.retention_text,
      });
      return;
    case 'automatizacion':
      await Promise.all([
        updateActivityCore(id, {
          has_automated_decision: draft.has_automated_decision,
          uses_ai: draft.uses_ai,
          own_systems_text: draft.own_systems_text,
        }),
        saveAutomatedDecisions(id, draft.automated_decisions),
      ]);
      return;
    case 'seguridad':
      await saveSecurityMeasures(id, draft.security_measures.map((code) => ({ measure: code })));
      return;
  }
}

export function ActivityWizard() {
  const { id: routeId } = useParams();
  const isEdit = !!routeId;
  const nav = useNavigate();
  const qc = useQueryClient();
  const { authz } = useAuth();

  const [id, setId] = useState<string | null>(routeId ?? null);
  const [draft, setDraft] = useState<WizardDraft>(EMPTY_WIZARD_DRAFT);
  const [stepIndex, setStepIndex] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [loadedStatus, setLoadedStatus] = useState<ActivityStatus | null>(null);
  const [hydrated, setHydrated] = useState(false);

  const existing = useQuery({
    queryKey: ['activity', routeId],
    queryFn: () => fetchActivity(routeId!),
    enabled: isEdit,
  });

  // Solo la PRIMERA carga hidrata `draft` desde el servidor. Guardar cada
  // paso invalida ['activity', id] a proposito (para que ActivitiesList/
  // ActivityDetail no queden con cache vieja) -- pero ese refetch en segundo
  // plano no debe pisar lo que el usuario ya esta escribiendo en un paso
  // siguiente, o cada avance de paso arriesga borrar el paso que viene.
  useEffect(() => {
    if (existing.data && !hydrated) {
      setDraft(detailToDraft(existing.data));
      setLoadedStatus(existing.data.status);
      setHydrated(true);
    }
  }, [existing.data, hydrated]);

  const assignable = useQuery({ queryKey: ['units', 'assignable'], queryFn: fetchAssignableUnits });

  // Misma cache key que invalida CatalogsAdmin.tsx al editar -- una edicion
  // de catalogo se refleja aqui sin recargar la pagina.
  const catalogsQ = useQuery({ queryKey: ['catalogs'], queryFn: fetchCatalogs });
  const catalogs = catalogsQ.data ?? FALLBACK_CATALOGS;

  const units = useMemo(() => {
    const own = authz.units.map((u) => ({ id: u.unit_id, name: u.name }));
    if (!assignable.data) return own;
    const seen = new Set(own.map((u) => u.id));
    return [...own, ...assignable.data.filter((u) => !seen.has(u.id))];
  }, [authz.units, assignable.data]);

  const stepKey = WIZARD_STEPS[stepIndex]!.key;
  const isLastDataStep = stepIndex === WIZARD_STEPS.length - 2;
  const isRevisionStep = stepIndex === WIZARD_STEPS.length - 1;

  function onChange(patch: Partial<WizardDraft>) {
    setDraft((d) => ({ ...d, ...patch }));
  }

  async function handleNext() {
    setErrors([]);
    if (isRevisionStep) return;

    const schema = wizardStepSchemas[stepKey as Exclude<WizardStepKey, 'revision'>];
    const result = schema.safeParse(draft);
    if (!result.success) {
      setErrors(result.error.issues.map((i) => i.message));
      return;
    }

    setSaving(true);
    try {
      let activityId = id;
      if (stepKey === 'identificacion' && !activityId) {
        activityId = await createActivityApi({
          responsible_unit_id: draft.responsible_unit_id!,
          title: draft.title,
          operational_owner_name: draft.operational_owner_name,
        });
        setId(activityId);
        nav(`/actividades/${activityId}/editar`, { replace: true });
      } else {
        await saveStep(activityId!, stepKey, draft, catalogs);
      }
      void qc.invalidateQueries({ queryKey: ['activity', activityId] });
      void qc.invalidateQueries({ queryKey: ['activities'] });
      setStepIndex((i) => Math.min(i + 1, WIZARD_STEPS.length - 1));
    } catch (e) {
      setErrors([e instanceof Error ? e.message : 'No se pudo guardar el paso.']);
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit() {
    setErrors([]);
    const schema = makeActivitySubmitSchema({
      sensitiveDataCategoryCodes: catalogs.data_category.filter((c) => c.is_sensitive === true).map((c) => c.code),
      reinforcedLegalBasisCodes: catalogs.legal_basis.filter((c) => c.requires_reinforced === true).map((c) => c.code),
    });
    const result = schema.safeParse(draft);
    if (!result.success) {
      setErrors(result.error.issues.map((i) => i.message));
      return;
    }
    setSaving(true);
    try {
      if (loadedStatus === 'OBSERVADO') {
        // El backend rechaza CORREGIDO mientras haya observaciones obligatorias sin responder por la unidad. Se
        // comprueba ANTES de tocar el estado: no queda la actividad a medias y se explica que falta y donde hacerlo.
        const pending = (await fetchOpenObservations(id!)).filter(
          (o) => o.severity === 'obligatoria' && (o.state === 'abierta' || o.state === 'reabierta'),
        );
        if (pending.length > 0) {
          const que = pending.length === 1 ? 'la observación obligatoria pendiente' : 'las ' + pending.length + ' observaciones obligatorias pendientes';
          setErrors([
            'Antes de reenviar a revisión, responda ' + que + ' en la ficha de la actividad (sección «Observaciones» → «Responder»). Sus cambios ya quedaron guardados.',
          ]);
          return;
        }
        await transitionActivity(id!, 'CORREGIDO');
      }
      await transitionActivity(id!, 'EN_REVISION');
      void qc.invalidateQueries({ queryKey: ['activity', id] });
      void qc.invalidateQueries({ queryKey: ['activities'] });
      nav(`/actividades/${id}`);
    } catch (e) {
      setErrors([e instanceof Error ? e.message : 'No se pudo enviar a revisión.']);
    } finally {
      setSaving(false);
    }
  }

  if (isEdit && existing.isLoading) return <p className="muted">Cargando…</p>;
  if (isEdit && loadedStatus && !EDITABLE_STATUS.has(loadedStatus)) {
    return (
      <>
        <Breadcrumbs items={[{ label: 'Actividades', to: '/actividades' }, { label: 'Actividad', to: `/actividades/${routeId}` }, { label: 'Editar' }]} />
        <PageHeader title="No editable" sub={`Esta actividad está en estado ${loadedStatus} y no admite edición.`} />
      </>
    );
  }

  const stepProps: StepProps = { draft, onChange };

  return (
    <>
      {/* Cada paso ya se guarda al avanzar, asi que salir no pierde nada: queda como Borrador en Actividades. */}
      <Breadcrumbs
        items={
          isEdit
            ? [{ label: 'Actividades', to: '/actividades' }, { label: 'Actividad', to: `/actividades/${routeId}` }, { label: 'Editar' }]
            : [{ label: 'Actividades', to: '/actividades' }, { label: 'Nueva actividad' }]
        }
      />
      <PageHeader
        eyebrow={`Paso ${stepIndex + 1} de ${WIZARD_STEPS.length}`}
        title={WIZARD_STEPS[stepIndex]!.label}
        sub="Actividad de tratamiento · Ley 21.719"
      />

      <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        {WIZARD_STEPS.map((s, i) => (
          <span
            key={s.key}
            className="pill"
            style={{
              background: i === stepIndex ? 'var(--accent-weak)' : 'var(--surface)',
              color: i === stepIndex ? 'var(--accent)' : 'var(--muted)',
              fontSize: 11,
            }}
          >
            {s.n}. {s.label}
          </span>
        ))}
      </div>

      <QueryState isLoading={false} error={isEdit ? existing.error : null}>
        <div className="card" style={{ padding: 18, maxWidth: 640 }}>
          {stepKey === 'identificacion' && <StepIdentificacion {...stepProps} units={units} unitLocked={!!id} />}
          {stepKey === 'finalidad' && <StepFinalidad {...stepProps} />}
          {stepKey === 'titulares' && <StepTitulares {...stepProps} catalogs={catalogs} />}
          {stepKey === 'base_juridica' && <StepBaseJuridica {...stepProps} catalogs={catalogs} />}
          {stepKey === 'destinatarios' && <StepDestinatarios {...stepProps} catalogs={catalogs} />}
          {stepKey === 'conservacion' && <StepConservacion {...stepProps} catalogs={catalogs} />}
          {stepKey === 'automatizacion' && <StepAutomatizacion {...stepProps} />}
          {stepKey === 'seguridad' && <StepSeguridad {...stepProps} catalogs={catalogs} />}
          {isRevisionStep && <RevisionSummary draft={draft} />}

          {errors.length > 0 && (
            <div className="callout callout--crit" style={{ marginTop: 10 }}>
              {errors.map((e, i) => (
                <div key={i}>{e}</div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
            <button className="btn btn--ghost" disabled={stepIndex === 0 || saving} onClick={() => setStepIndex((i) => Math.max(0, i - 1))}>
              Anterior
            </button>
            {!isRevisionStep ? (
              <button className="btn btn--primary" disabled={saving} onClick={() => void handleNext()}>
                {saving ? 'Guardando…' : isLastDataStep ? 'Guardar y revisar' : 'Guardar y continuar'}
              </button>
            ) : (
              <button className="btn btn--primary" disabled={saving || !id} onClick={() => void handleSubmit()}>
                {saving ? 'Enviando…' : 'Enviar a revisión'}
              </button>
            )}
          </div>
        </div>
      </QueryState>
    </>
  );
}

function RevisionSummary({ draft }: { draft: WizardDraft }) {
  return (
    <div style={{ display: 'grid', gap: 10, fontSize: 13 }}>
      <p className="muted">Revise los datos antes de enviar a revisión del DPD.</p>
      <div>
        <b>{draft.title || '(sin nombre)'}</b>
        <p className="muted">{draft.purpose}</p>
      </div>
      <div>Categorías de titulares: {draft.subject_categories.length}</div>
      <div>Categorías de datos: {draft.data_categories.length}</div>
      <div>Bases de licitud: {draft.legal_bases.length}</div>
      <div>Destinatarios: {draft.recipients.length}</div>
      <div>Transferencias: {draft.transfers.length}</div>
      <div>Medidas de seguridad: {draft.security_measures.length}</div>
    </div>
  );
}
